import { useMemo } from 'react';
import type { FloatBin } from '../../services/analytics';
import { ChartEmpty, niceTicks, useMeasure, useTooltip } from './chartPrimitives';

interface FloatHistogramProps {
  bins: FloatBin[];
  onSelectBin?: (activityIds: string[], label: string) => void;
}

const SEVERITY_COLOUR: Record<FloatBin['severity'], string> = {
  negative: 'var(--status-fail)',
  critical: 'var(--mr-vivid-blue)',
  healthy: 'var(--data-3)',
  excess: 'var(--status-warn)',
};

const SEVERITY_MEANING: Record<FloatBin['severity'], string> = {
  negative: 'Behind — cannot meet the imposed date',
  critical: 'On the critical path',
  healthy: 'Working slack',
  excess: 'Above the DCMA 44-day threshold — usually missing logic',
};

/**
 * Distribution of total float across incomplete activities.
 *
 * The shape is the diagnosis: a spike at zero with nothing either side means
 * an over-constrained schedule; a long tail past 44 days means missing
 * successor logic rather than genuine slack.
 */
export function FloatHistogram({ bins, onSelectBin }: FloatHistogramProps) {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const tooltip = useTooltip();

  const total = useMemo(() => bins.reduce((sum, b) => sum + b.count, 0), [bins]);

  if (total === 0) {
    return <ChartEmpty message="No incomplete activities to profile." />;
  }

  const height = 250;
  const margin = { top: 14, right: 16, bottom: 42, left: 44 };
  const plotWidth = Math.max(0, width - margin.left - margin.right);
  const plotHeight = height - margin.top - margin.bottom;

  const maxCount = Math.max(...bins.map((b) => b.count));
  const ticks = niceTicks(maxCount, 4);
  const yMax = ticks[ticks.length - 1] || 1;

  const bandWidth = plotWidth / bins.length;
  const barWidth = Math.max(2, bandWidth - 8);

  // The 44-day DCMA line sits on the boundary between the 21–44 and 45–100 bins.
  const thresholdIndex = bins.findIndex((b) => b.severity === 'excess');
  const thresholdX = thresholdIndex > 0 ? thresholdIndex * bandWidth : null;

  return (
    <div ref={ref} className="w-full">
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Total float distribution">
          <g transform={`translate(${margin.left},${margin.top})`}>
            {ticks.map((tick) => {
              const y = plotHeight - (tick / yMax) * plotHeight;
              return (
                <g key={tick}>
                  <line x1={0} x2={plotWidth} y1={y} y2={y} className="chart-grid-line" />
                  <text x={-8} y={y + 3} textAnchor="end" className="chart-axis-label">
                    {tick}
                  </text>
                </g>
              );
            })}

            {thresholdX !== null && (
              <g>
                <line
                  x1={thresholdX}
                  x2={thresholdX}
                  y1={-6}
                  y2={plotHeight}
                  stroke="var(--status-warn)"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                />
                <text
                  x={thresholdX + 5}
                  y={-1}
                  className="chart-axis-label"
                  style={{ fill: 'var(--status-warn)' }}
                >
                  DCMA 44d
                </text>
              </g>
            )}

            {bins.map((bin, index) => {
              const barHeight = (bin.count / yMax) * plotHeight;
              const x = index * bandWidth + (bandWidth - barWidth) / 2;
              const y = plotHeight - barHeight;
              const colour = SEVERITY_COLOUR[bin.severity];
              const share = ((bin.count / total) * 100).toFixed(1);

              return (
                <g key={bin.label}>
                  {/* Full-height hit area so thin bars stay clickable. */}
                  <rect
                    x={index * bandWidth}
                    y={0}
                    width={bandWidth}
                    height={plotHeight}
                    fill="transparent"
                    className={bin.count > 0 ? 'chart-hit' : undefined}
                    onMouseMove={(event) =>
                      tooltip.show(
                        <div>
                          <div className="font-semibold">{bin.label} days float</div>
                          <div className="tnum">
                            {bin.count} activities · {share}%
                          </div>
                          <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                            {SEVERITY_MEANING[bin.severity]}
                          </div>
                          {bin.count > 0 && onSelectBin && (
                            <div style={{ color: 'var(--mr-vivid-blue)', marginTop: 4 }}>
                              Click to filter
                            </div>
                          )}
                        </div>,
                        event
                      )
                    }
                    onMouseLeave={tooltip.hide}
                    onClick={() =>
                      bin.count > 0 &&
                      onSelectBin?.(bin.activityIds, `Float ${bin.label} days`)
                    }
                  />
                  {bin.count > 0 && (
                    <rect
                      x={x}
                      y={y}
                      width={barWidth}
                      height={barHeight}
                      fill={colour}
                      rx={1}
                      className="series-bar pointer-events-none"
                      style={{ animationDelay: `${index * 45}ms` }}
                    />
                  )}
                  <text
                    x={index * bandWidth + bandWidth / 2}
                    y={plotHeight + 15}
                    textAnchor="middle"
                    className="chart-axis-label"
                  >
                    {bin.label}
                  </text>
                </g>
              );
            })}

            <text
              x={plotWidth / 2}
              y={plotHeight + 34}
              textAnchor="middle"
              className="chart-axis-label"
            >
              TOTAL FLOAT (WORKING DAYS)
            </text>
          </g>
        </svg>
      )}
    </div>
  );
}
